import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import './ImagePreview.css';

/**
 * ImagePreview Component
 * Displays a preview of an image from a URL with loading and error states
 *
 * Props:
 * - url: string - Image URL to preview
 * - alt: string - Alt text for the image
 * - aspectRatio: string - Aspect ratio (default: "16:9")
 * - onError: (message) => void - Called when image fails to load
 * - onLoad: () => void - Called when image loads successfully
 */
function ImagePreview({
  url,
  alt = 'Image preview',
  aspectRatio = '16:9',
  onError,
  onLoad,
}) {
  const [status, setStatus] = useState('idle'); // idle, loading, loaded, error
  const [showSpinner, setShowSpinner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const imgRef = useRef(null);
  const spinnerTimeoutRef = useRef(null);

  // Calculate aspect ratio padding
  const getAspectRatioPadding = () => {
    const [width, height] = aspectRatio.split(':').map(Number);
    return `${(height / width) * 100}%`;
  };

  // Check for cached image after DOM paint
  useLayoutEffect(() => {
    const img = imgRef.current;
    if (url && img && img.complete && img.naturalWidth > 0) {
      setStatus('loaded');
      setShowSpinner(false);
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
      }
    }
  });

  // Reset state when URL changes
  useEffect(() => {
    if (!url) {
      setStatus('idle');
      setShowSpinner(false);
      setErrorMessage('');
      return;
    }

    // Check if image is already cached/complete
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setStatus('loaded');
      setShowSpinner(false);
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    // Only show spinner after 150ms delay to avoid flash for fast-loading images
    spinnerTimeoutRef.current = setTimeout(() => {
      setShowSpinner(true);
    }, 150);

    return () => {
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
      }
    };
  }, [url]);

  const handleImageLoad = () => {
    if (spinnerTimeoutRef.current) {
      clearTimeout(spinnerTimeoutRef.current);
    }
    setStatus('loaded');
    setShowSpinner(false);
    if (onLoad) {
      onLoad();
    }
  };

  const handleImageError = () => {
    if (spinnerTimeoutRef.current) {
      clearTimeout(spinnerTimeoutRef.current);
    }
    setStatus('error');
    setShowSpinner(false);
    const message = 'Failed to load image. Please check the URL is valid and accessible.';
    setErrorMessage(message);
    if (onError) {
      onError(message);
    }
  };

  // Empty state
  if (!url) {
    return (
      <div className="image-preview image-preview--empty">
        <div
          className="image-preview__container"
          style={{ paddingBottom: getAspectRatioPadding() }}
        >
          <div className="image-preview__placeholder">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1}
              stroke="currentColor"
              className="placeholder-icon"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
              />
            </svg>
            <span>Enter an image URL to see preview</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`image-preview image-preview--${status}`}>
      <div
        className="image-preview__container"
        style={{ paddingBottom: getAspectRatioPadding() }}
      >
        {/* Loading State - only show spinner after delay */}
        {status === 'loading' && showSpinner && (
          <div className="image-preview__loading">
            <div className="loading-spinner" />
            <span>Loading preview...</span>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="image-preview__error">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="error-icon"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
            <span className="error-title">Failed to load image</span>
            <span className="error-message">{errorMessage}</span>
          </div>
        )}

        {/* Image */}
        <img
          ref={imgRef}
          src={url}
          alt={alt}
          className={`image-preview__img ${status === 'loaded' ? 'image-preview__img--visible' : ''}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </div>
    </div>
  );
}

export default ImagePreview;
